<script setup lang="ts">
import { bemBuilder, toMoney } from "@chatfood/core-utils";
import { ref, computed } from "vue";
import type { ISatLoyaltyProgramSelectionProps } from "@chatfood/design-system";
import {
  AtomText,
  AtomLoadingSpinner,
  SatIconButton,
  SatLoyaltyProgramSelection,
  useToast,
  useTogglable,
  SatTooltip,
  SatElasticPanel,
} from "@chatfood/design-system";
import { FetchError } from "ofetch";
import { useLocale } from "~/composables/use-locale";
import { useBasketStore } from "~/stores/basket";
import type { IDomainLoyaltyProgram } from "~/domain/loyalty-program";
import type { IDomainAppliedLoyalty } from "~/domain/applied-loyalty";
import { BASE_TELEPORT_SELECTOR } from "~/domain/base-teleport-selector";
import { useBugReporter, useDirectBackend } from "#imports";

const css = bemBuilder("loyalty-program-handler");

const { t } = useLocale();

const {
  state: loyaltyProgramSelectionState,
  enable: showLoyaltyProgramSelection,
  disable: hideLoyaltyProgramSelection,
} = useTogglable();

const { report } = useBugReporter();
const { toastError, toastSuccess } = useToast();

const isApplyingLoyalty = ref(false);
const isRemovingLoyaltyProgram = ref(false);

const loyaltyPrograms = ref<ISatLoyaltyProgramSelectionProps["items"]>([]);

const basket = useBasketStore();

const appliedLoyalties = computed(() =>
  basket.loyalties.filter(
    (loyalty) => loyalty.category !== "loyalty_wallet_amount",
  ),
);

const getBadgeLabel = (program: IDomainLoyaltyProgram): string => {
  if (["free_item", "loyalty_free_item"].includes(program.type)) {
    return t("components.loyalty_program_handler.badge_free_item");
  }

  if (["buy_one_get_one_free", "loyalty_bogo"].includes(program.type)) {
    return t("components.loyalty_program_handler.badge_bogo");
  }

  if (
    program.type === "discount_percentage" ||
    program.type === "item_discount_percentage"
  ) {
    return t("components.loyalty_program_handler.badge_discount_percentage", {
      percentage: program.discountPercentage,
    });
  }

  if (
    program.type === "discount_amount" ||
    program.type === "item_discount_amount"
  ) {
    return toMoney(program.discount.value, basket.currency);
  }

  if (["buy_one_get_one_free", "loyalty_bogo"].includes(program.type)) {
    return t("components.loyalty_program_handler.badge_bogo");
  }

  return "";
};

const getBadgeType = (
  type: IDomainLoyaltyProgram["type"] | IDomainAppliedLoyalty["category"],
): ISatLoyaltyProgramSelectionProps["items"][0]["badgeType"] => {
  if (["free_item", "loyalty_free_item"].includes(type)) {
    return "warning";
  }

  if (["buy_one_get_one_free", "loyalty_bogo"].includes(type)) {
    return "danger";
  }

  return "positive";
};

const isFetchingPrograms = ref(false);

const { basketRepo } = useDirectBackend();

async function getLoyaltyPrograms() {
  isFetchingPrograms.value = true;

  try {
    const res = await basketRepo.getLoyaltyPrograms({
      businessId: basket.businessId,
      outletId: basket.outletId,
      orderingMode: basket.orderingMode,
      salesChannel: basket.salesChannel,
    });

    loyaltyPrograms.value = res.data
      .filter((program) => program.isApplicable)
      .map((program) => ({
        id: program.id,
        title: program.title,
        photo: program.photo ?? undefined,
        cost: program.cost ?? undefined,
        description: program.description,
        badgeLabel: getBadgeLabel(program),
        badgeType: getBadgeType(program.type),
        expiresAt: program.expiresAt,
      }));
  } catch (e) {
    loyaltyPrograms.value = [];
    report(e);
  } finally {
    isFetchingPrograms.value = false;
  }
}

function openSelection() {
  getLoyaltyPrograms();
  showLoyaltyProgramSelection();
}

function isLoyaltyApplied(programId: string) {
  if (basket.loyalties.find((loyalty) => loyalty.id === programId)) {
    toastSuccess(t("components.loyalty_program_handler.apply_message_success"));
  } else {
    toastError(t("components.loyalty_program_handler.apply_message_error"));
  }
}

async function onSelect(programId: string) {
  isApplyingLoyalty.value = true;

  try {
    await basket.applyLoyalty(programId);

    isLoyaltyApplied(programId);

    hideLoyaltyProgramSelection();
  } catch (error: unknown) {
    const applyErrorMessages: Record<string, string> = {
      loyalty_already_applied: t(
        "components.loyalty_program_handler.apply_message_error_already_applied",
      ),
      loyalty_not_eligible: t(
        "components.loyalty_program_handler.apply_message_error_not_eligible",
      ),
    };

    let errorMessage = t(
      "components.loyalty_program_handler.apply_message_error",
    );

    if (
      error instanceof FetchError &&
      error.statusCode === 422 &&
      error.data?.statusMessage
    ) {
      const statusMessage = applyErrorMessages[error.data.statusMessage];
      errorMessage = statusMessage ?? errorMessage;
    }

    toastError(errorMessage);
  } finally {
    isApplyingLoyalty.value = false;
  }
}

async function onRemove(programId: string) {
  isRemovingLoyaltyProgram.value = true;

  try {
    await basket.removeLoyalty(programId);
  } catch (e) {
    report(e);
    toastError(t("components.loyalty_program_handler.remove_message_error"));
  } finally {
    isRemovingLoyaltyProgram.value = false;
  }
}

const disabledMessageTooltip = computed(() => {
  if (basket.discount) {
    return t("components.loyalty_program_handler.reward_cta_tooltip_discount");
  }

  if (basket.loyaltyWallet) {
    return t("components.loyalty_program_handler.reward_cta_tooltip_loyalty");
  }

  return "";
});

const colorText = computed(() =>
  disabledMessageTooltip.value ? "color-text-disabled" : "color-primary",
);
</script>

<template>
  <div :class="css()">
    <SatTooltip :content="disabledMessageTooltip">
      <div
        data-test="box"
        :class="[
          css('box'),
          { [css('box', 'disabled')]: Boolean(disabledMessageTooltip) },
        ]"
      >
        <a
          v-if="!appliedLoyalties.length"
          href=""
          :class="css('open-selection')"
          data-test="cta-open-selection"
          :disabled="Boolean(disabledMessageTooltip)"
          @click.prevent="openSelection"
        >
          <AtomText type="body-bold" :color="colorText">
            {{
              t("components.loyalty_program_handler.cta_open_selection_label")
            }}
          </AtomText>
        </a>
        <div
          v-for="program in appliedLoyalties"
          :key="program.id"
          :data-test="`program-card-${program.id}`"
          :class="css('content')"
        >
          <div :class="css('filled')">
            <AtomText type="body-bold">{{ program.name }}</AtomText>
            <SatIconButton
              v-if="!isRemovingLoyaltyProgram"
              size="extra-small"
              variant="danger"
              type="rounded"
              theme="ghost"
              icon-name="delete"
              @click.prevent="onRemove(program.id)"
            />
            <AtomLoadingSpinner v-else :size="21" />
          </div>
        </div>
      </div>
    </SatTooltip>
    <teleport :to="BASE_TELEPORT_SELECTOR">
      <SatElasticPanel
        v-model="loyaltyProgramSelectionState"
        show-close
        width-fit-content
        padding="none"
      >
        <SatLoyaltyProgramSelection
          :class="css('selection')"
          :title="t('components.loyalty_program_handler.selector.title')"
          :items="loyaltyPrograms"
          :button-label="
            t('components.loyalty_program_handler.selector.cta_label')
          "
          :empty-message="
            t('components.loyalty_program_handler.selector.empty_message')
          "
          :is-loading="isFetchingPrograms"
          :is-button-loading="isApplyingLoyalty"
          @selected="(item) => onSelect(item.id)"
        />
      </SatElasticPanel>
    </teleport>
  </div>
</template>

<style lang="scss" scoped>
.loyalty-program-handler {
  display: flex;
  flex-direction: column;

  &__box {
    width: 100%;
    min-height: 51px;
    border-radius: var(--radii-card-sm);
    border: 1px solid var(--color-base-90);
    background: var(--color-base-100);

    &--disabled {
      pointer-events: none;
      cursor: no-drop;
      background: var(--color-base-95);
    }
  }

  &__open-selection {
    display: block;
    padding: var(--spacing-md);
  }

  &__content {
    display: flex;
    gap: 14px;
    align-items: center;
    justify-content: space-between;
  }

  &__selection {
    max-width: 100%;
    min-height: 30dvh;

    @media (width > $breakpoint-sm) {
      width: 470px;
    }
  }

  &__filled {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    gap: var(--spacing-sm);
    min-height: 45px;
    border: 1px solid var(--color-base-95);
    border-radius: var(--radii-card-sm);
    padding: var(--spacing-md);
  }
}
</style>
